/**
 * ipLocation.js
 *
 * Approximate location of the current visitor from their public IP address —
 * no browser geolocation prompt. Used to stamp portal logins and page views so
 * the CRM Client overview → Access tab can show where each session came from
 * (e.g. spotting a shared login).
 *
 * IP geolocation is city-level at best: mobile carriers and VPNs often resolve
 * to a hub city, so treat it as a signal, not proof.
 *
 * Best-effort: resolves to null on any failure (ad blockers, rate limits,
 * offline) and never throws.
 */

const CACHE_KEY = 'bba_ip_location_v1';
// Re-resolve after this long so a laptop that changes networks gets a fresh fix
const CACHE_TTL_MS = 30 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 4000;

const PROVIDERS = [
  {
    url: 'https://ipapi.co/json/',
    parse: (d) =>
      d && !d.error && d.ip
        ? { ip: d.ip, city: d.city, region: d.region, country: d.country_name || d.country, org: d.org }
        : null,
  },
  {
    url: 'https://ipwho.is/',
    parse: (d) =>
      d && d.success !== false && d.ip
        ? { ip: d.ip, city: d.city, region: d.region, country: d.country, org: d.connection?.org || d.connection?.isp }
        : null,
  },
];

let inflight = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.value || Date.now() - cached.savedAt > CACHE_TTL_MS) return null;
    return cached.value;
  } catch {
    return null;
  }
}

function writeCache(value) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    // Storage blocked — the in-memory promise still dedupes for this page load
  }
}

async function fetchWithTimeout(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/** Drop empty fields so Firestore docs stay tidy. */
function clean(loc) {
  const out = {};
  Object.entries(loc).forEach(([k, v]) => {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  });
  return out.ip ? out : null;
}

/**
 * @returns {Promise<{ip: string, city?: string, region?: string, country?: string, org?: string}|null>}
 */
export function getIpLocation() {
  const cached = readCache();
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = (async () => {
    for (const provider of PROVIDERS) {
      try {
        const parsed = provider.parse(await fetchWithTimeout(provider.url));
        const value = parsed && clean(parsed);
        if (value) {
          writeCache(value);
          return value;
        }
      } catch {
        // Try the next provider
      }
    }
    return null;
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

/**
 * Resolve the location but give up after `ms` so callers on a critical path
 * (sign-in) never wait on a slow lookup.
 */
export function getIpLocationWithin(ms) {
  return Promise.race([getIpLocation(), new Promise((resolve) => setTimeout(() => resolve(null), ms))]);
}

/** 'Austin, Texas, United States' — or null when nothing useful resolved. */
export function formatIpLocation(loc) {
  if (!loc) return null;
  const parts = [loc.city, loc.region, loc.country].filter(Boolean);
  // Dedupe e.g. 'Singapore, Singapore'
  return parts.filter((p, i) => parts.indexOf(p) === i).join(', ') || null;
}
